import React from "react";
import { useSimulator } from "@/context/SimulatorContext";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { fmtEUR, fmtNum, fmtPct } from "@/lib/format";
import { OPTIMIZER_SURFACES, OPTIMIZER_VOLUMES } from "@/lib/defaults";
import { Target, TrendingUp, AlertCircle, CheckCircle2 } from "lucide-react";

export const OptimizerTab = () => {
  const { optimizer, inputs } = useSimulator();
  const { combos, optimalKey } = optimizer;

  // Group combos by surface for a compact grid
  const bySurface = OPTIMIZER_SURFACES.map((surface) => ({
    surface,
    rows: combos.filter((c) => c.surface_m2 === surface),
  }));

  const positive = combos.filter((c) => c.ebitda >= 0);
  const currentCfg = combos.find(
    (c) => c.surface_m2 === inputs.saffron.surface_m2 && c.pbr_volume_m3 === inputs.algae.pbr_volume_m3
  );

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="flex items-start gap-4">
          <div className="rounded-xl bg-indigo-500/10 border border-indigo-500/30 p-3">
            <Target className="h-7 w-7 text-indigo-400" />
          </div>
          <div>
            <h2 className="font-display text-2xl font-bold text-slate-100">
              MVP Optimizer — Configurazioni Confronto
            </h2>
            <p className="text-sm text-slate-400 mt-1 max-w-2xl">
              Grid comparativa 3 × 4: Lotto 1 su {OPTIMIZER_SURFACES.join(", ")} m² × Lotto 2 su {OPTIMIZER_VOLUMES.join(", ")} m³.
              La cella evidenziata è la <span className="text-emerald-400 font-semibold">configurazione minima con EBITDA ≥ 0</span>.
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2">
          {positive.length > 0 ? (
            <Badge className="bg-emerald-500/15 text-emerald-300 border border-emerald-500/40">
              <CheckCircle2 className="h-3 w-3 mr-1" />
              {positive.length} config redditizie su {combos.length}
            </Badge>
          ) : (
            <Badge className="bg-rose-500/15 text-rose-300 border border-rose-500/40">
              <AlertCircle className="h-3 w-3 mr-1" />
              Nessuna config EBITDA ≥ 0
            </Badge>
          )}
        </div>
      </div>

      {/* Optimizer detailed table */}
      <Card className="bg-slate-900/70 border-slate-800 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left border-b border-slate-800 bg-slate-950/40">
                <Th>Lotto 1</Th>
                <Th>Lotto 2</Th>
                <Th className="text-right">Zaff. kg/anno</Th>
                <Th className="text-right">Microalghe kg</Th>
                <Th className="text-right">Ricavo</Th>
                <Th className="text-right">OPEX</Th>
                <Th className="text-right">EBITDA</Th>
                <Th className="text-right">Margin %</Th>
                <Th className="text-right">CAPEX</Th>
                <Th className="text-right">ΔCAPEX</Th>
                <Th className="text-right">ROI 5Y</Th>
                <Th className="text-right">Payback</Th>
                <Th className="text-right">Break-even/m</Th>
              </tr>
            </thead>
            <tbody>
              {combos.map((c) => {
                const isOptimal = c.key === optimalKey;
                const isCurrent = currentCfg && c.key === currentCfg.key;
                const positive = c.ebitda >= 0;
                return (
                  <tr
                    key={c.key}
                    data-testid={`optimizer-row-${c.key}`}
                    className={`border-b border-slate-800/60 hover:bg-slate-800/20 transition-colors ${
                      isOptimal ? "bg-emerald-500/10 border-emerald-500/30" : ""
                    } ${isCurrent && !isOptimal ? "bg-sky-500/5 border-sky-500/20" : ""}`}
                  >
                    <Td className="font-semibold text-slate-100">
                      <div className="flex items-center gap-2">
                        <span className="font-mono-tab">{c.surface_m2} m²</span>
                        {isOptimal && (
                          <Badge className="bg-emerald-500 text-slate-950 text-[10px] px-1.5 py-0">MIN EBITDA≥0</Badge>
                        )}
                        {isCurrent && !isOptimal && (
                          <Badge className="bg-sky-500 text-slate-950 text-[10px] px-1.5 py-0">Corrente</Badge>
                        )}
                      </div>
                    </Td>
                    <Td className="font-mono-tab text-slate-100">{c.pbr_volume_m3} m³</Td>
                    <Td className="text-right font-mono-tab text-amber-400">{fmtNum(c.saffron_kg, 1)}</Td>
                    <Td className="text-right font-mono-tab text-emerald-400">{fmtNum(c.algae_kg_total, 0)}</Td>
                    <Td className="text-right font-mono-tab text-slate-100">{fmtEUR(c.revenue)}</Td>
                    <Td className="text-right font-mono-tab text-rose-400">{fmtEUR(c.opex)}</Td>
                    <Td className={`text-right font-mono-tab font-semibold ${positive ? "text-emerald-300" : "text-rose-400"}`}>
                      {fmtEUR(c.ebitda)}
                    </Td>
                    <Td className={`text-right font-mono-tab ${positive ? "text-emerald-400" : "text-rose-500"}`}>
                      {fmtPct(c.ebitdaMargin, 1)}
                    </Td>
                    <Td className="text-right font-mono-tab text-violet-400">{fmtEUR(c.capex_total)}</Td>
                    <Td className={`text-right font-mono-tab ${c.capex_delta >= 0 ? "text-slate-400" : "text-emerald-400"}`}>
                      {c.capex_delta >= 0 ? "+" : ""}{fmtEUR(c.capex_delta)}
                    </Td>
                    <Td className={`text-right font-mono-tab ${c.roi5y >= 0 ? "text-sky-400" : "text-rose-500"}`}>
                      {fmtPct(c.roi5y, 1)}
                    </Td>
                    <Td className="text-right font-mono-tab text-slate-300">
                      {c.payback ? `${c.payback.toFixed(1)} anni` : "—"}
                    </Td>
                    <Td className="text-right font-mono-tab text-slate-300">
                      {c.breakeven_monthly ? fmtEUR(c.breakeven_monthly) : "—"}
                    </Td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </Card>

      {/* Heatmap grid by surface × volume */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {bySurface.map((sg) => (
          <Card key={sg.surface} className="bg-slate-900/70 border-slate-800 p-5">
            <div className="flex items-center justify-between mb-3">
              <div className="font-display text-lg font-semibold text-amber-300">
                Lotto 1 · {sg.surface} m²
              </div>
              <Badge className="bg-amber-500/10 text-amber-300 border border-amber-500/30 text-[10px]">
                {(sg.surface * inputs.saffron.levels).toLocaleString("it-IT")} m² eq.
              </Badge>
            </div>
            <div className="space-y-2">
              {sg.rows.map((c) => {
                const isOptimal = c.key === optimalKey;
                const positive = c.ebitda >= 0;
                return (
                  <div
                    key={c.key}
                    data-testid={`optimizer-cell-${c.key}`}
                    className={`p-3 rounded-lg border transition-all ${
                      isOptimal
                        ? "border-emerald-500/60 bg-emerald-500/10 shadow-[0_0_15px_rgba(16,185,129,0.2)]"
                        : positive
                        ? "border-slate-700 bg-slate-950/50"
                        : "border-slate-800 bg-slate-950/30 opacity-70"
                    }`}
                  >
                    <div className="flex items-center justify-between text-xs">
                      <span className="font-semibold text-slate-300">{c.pbr_volume_m3} m³ PBR</span>
                      {isOptimal && <Badge className="bg-emerald-500 text-slate-950 text-[9px] px-1 py-0">★ MIN</Badge>}
                    </div>
                    <div className={`mt-1 font-mono-tab font-bold text-lg ${positive ? "text-emerald-300" : "text-rose-400"}`}>
                      {fmtEUR(c.ebitda, { compact: true })}
                    </div>
                    <div className="text-[10px] text-slate-500 font-mono-tab flex justify-between">
                      <span>Ricavo {fmtEUR(c.revenue, { compact: true })}</span>
                      <span>{c.payback ? `${c.payback.toFixed(1)}y` : "—"}</span>
                    </div>
                  </div>
                );
              })}
            </div>
          </Card>
        ))}
      </div>

      {/* Recommendation */}
      {optimalKey && (
        <Card data-testid="optimizer-recommendation" className="bg-gradient-to-br from-emerald-500/15 to-teal-500/5 border border-emerald-500/40 p-6">
          <div className="flex items-start gap-4">
            <div className="p-2 rounded-lg bg-emerald-500/20 border border-emerald-500/40">
              <TrendingUp className="h-5 w-5 text-emerald-400" />
            </div>
            <div className="flex-1">
              <div className="text-xs font-semibold uppercase tracking-widest text-emerald-300">
                Configurazione minima consigliata
              </div>
              <div className="font-display text-2xl font-bold text-emerald-200 mt-1">
                {combos.find((c) => c.key === optimalKey).surface_m2} m² zafferano · {combos.find((c) => c.key === optimalKey).pbr_volume_m3} m³ PBR
              </div>
              <div className="mt-2 text-sm text-slate-300">
                Raggiunge <span className="text-emerald-400 font-mono-tab font-semibold">EBITDA {fmtEUR(combos.find((c) => c.key === optimalKey).ebitda)}</span>
                {" "}con CAPEX di{" "}
                <span className="text-violet-300 font-mono-tab font-semibold">{fmtEUR(combos.find((c) => c.key === optimalKey).capex_total)}</span>,
                payback {combos.find((c) => c.key === optimalKey).payback ? `${combos.find((c) => c.key === optimalKey).payback.toFixed(1)} anni` : "N/D"}.
              </div>
            </div>
          </div>
        </Card>
      )}
    </div>
  );
};

const Th = ({ children, className = "" }) => (
  <th className={`px-3 py-2.5 text-[10px] font-semibold uppercase tracking-wider text-slate-400 whitespace-nowrap ${className}`}>
    {children}
  </th>
);
const Td = ({ children, className = "" }) => (
  <td className={`px-3 py-2.5 whitespace-nowrap ${className}`}>{children}</td>
);
